<template>
  <div class="blog-list-view landing-page">
    <div class="content-wrapper">
      <section class="section">
        <div class="container">
          <SectionTitle
            :title="$t('blog.title')"
            :subtitle="$t('blog.subtitle')"
          />
          <div class="columns is-multiline">
            <template
              v-for="(post, idx) in posts"
              :key="post._id"
            >
              <div
                v-if="adIndices.includes(idx)"
                class="column is-12-mobile is-6-tablet is-4-desktop"
              >
                <AdSenseUnit
                  ad-slot="1037369901"
                  ad-format="fluid"
                  ad-layout-key="-4w+cd-2c-ee+178"
                  container-class="in-feed"
                />
              </div>

              <div
                class="column is-12-mobile is-6-tablet is-4-desktop animate"
              >
                <article class="card blog-card improved-card">
                  <div class="blog-image-wrapper">
                    <figure class="image is-2by1 blog-image-banner">
                      <img
                        :src="post.image"
                        :alt="post.title"
                        loading="lazy"
                      >
                      <span
                        v-if="post.category"
                        class="tag is-info blog-category-badge"
                      >
                        {{ $i18n.locale === 'es' ? post.category.name_es : post.category.name }}
                      </span>
                      <div class="blog-image-gradient" />
                    </figure>
                  </div>
                  <div class="card-content">
                    <router-link
                      tag-name="h2"
                      :to="{ name: 'blog-post', params: { id: post.key } }"
                      class="title is-5 mb-3 blog-title"
                    >
                      {{ $i18n.locale === 'es' ? post.title_es : post.title }}
                    </router-link>
                    <div class="is-flex is-align-items-center mb-3 mt-3 blog-meta-row">
                      <figure class="image is-32x32 mr-2">
                        <img
                          onerror="this.src = '/assets/images/img-placeholder.jpg'"
                          :src="post.author.profile_image"
                          :alt="post.author.name"
                          class="is-rounded"
                        >
                      </figure>
                      <span class="has-text-weight-semibold">{{ post.author.name }}</span>
                      <span class="mx-2">·</span>
                      <span class="has-text-grey is-size-7">{{ formatDate(post.publishing_date) }}</span>
                    </div>
                    <p class="subtitle is-6 mb-3 blog-description">
                      {{ $i18n.locale === 'es' ? post.description_es : post.description }}
                    </p>
                    <div class="is-flex is-justify-content-flex-end">
                      <router-link
                        :to="{ name: 'blog-post', params: { id: post.key } }"
                        class="button blog-readmore-btn"
                        :aria-label="$t('blog.readMore')"
                      >
                        {{ $t('blog.readMore') }}
                        <i
                          class="fas fa-arrow-right ml-2"
                          aria-hidden="true"
                        />
                      </router-link>
                    </div>
                  </div>
                </article>
              </div>
            </template>
          </div>
          <Pagination
            :current-page="page"
            :total-pages="totalPages"
            aria-label="Blog pagination"
            @update:current-page="goToPage"
          />
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import blogService from '@/services/blogService';
import SectionTitle from '@/components/ui/SectionTitle.vue';
import Pagination from '@/components/common/Pagination.vue';
import AdSenseUnit from '@/components/ui/AdSenseUnit.vue';
import adsenseService from '@/services/adsenseService.js';
import { usePageSeo } from '@/composables/usePageSeo';
import { SITE_URL, SITE_NAME, buildBreadcrumbSchema } from '@/config/seo';

const { t, locale } = useI18n();
const route = useRoute();
const router = useRouter();
const posts = ref([]);
const adIndices = ref([]);
const page = ref(Number(route.query.page) || 1);
const total = ref(0);
const results = ref(0);
const totalPages = ref(1);
const loading = ref(true);

const getRandomImage = () => {
  const random = Math.floor(Math.random() * 5) + 1;
  return `/assets/images/blog-${random}.jpg`;
};

const fetchPosts = async () => {
  loading.value = true;
  try {
    const res = await blogService.getAll({ page: page.value, limit: 9, published: 1 });
    res.items.forEach(item => {
      if (!item.image) {
        item.image = getRandomImage();
      }
    });

    posts.value = res.items;
    adIndices.value = generateRandomAdIndices(posts.value.length, 3);
    total.value = res.total;
    results.value = res.results;
    totalPages.value = Math.ceil(res.total / res.results);
    await nextTick();
    adsenseService.refreshAds();
  } catch (e) {
    posts.value = [];
    adIndices.value = [];
    total.value = 0;
    results.value = 0;
    totalPages.value = 1;
  } finally {
    loading.value = false;
  }
};

const goToPage = (n) => {
  if (n < 1 || n > totalPages.value) return;
  router.push({ name: 'blog', query: { page: n } });
};

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString(locale.value, { year: 'numeric', month: 'short', day: 'numeric' });
};

onMounted(fetchPosts);

watch(() => route.query.page, (val) => {
  page.value = Number(val) || 1;
  fetchPosts();
});

const blogJsonLd = computed(() => ({
  '@context': 'https://schema.org',
  '@type': 'Blog',
  name: t('blog.page_title'),
  description: t('blog.description'),
  url: `${SITE_URL}/blog`,
  publisher: {
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
  },
  blogPost: posts.value.slice(0, 10).map((post) => ({
    '@type': 'BlogPosting',
    headline: locale.value === 'es' ? (post.title_es || post.title) : post.title,
    description: locale.value === 'es' ? (post.description_es || post.description) : post.description,
    image: post.image,
    datePublished: post.publishing_date,
    author: post.author?.name ? { '@type': 'Person', name: post.author.name } : undefined,
    url: `${SITE_URL}/blog/${post.key}`,
  })),
}));

const breadcrumbsJsonLd = computed(() => buildBreadcrumbSchema([
  { name: SITE_NAME, url: '/' },
  { name: t('blog.title'), url: '/blog' },
]));

usePageSeo({
  titleKey: 'blog.page_title',
  descriptionKey: 'blog.description',
  ogType: 'website',
  jsonLd: computed(() => [blogJsonLd.value, breadcrumbsJsonLd.value]),
});

function generateRandomAdIndices(totalItems, minAds) {
  const indices = new Set();
  if (totalItems <= 1) return [];
  const maxAds = Math.min(minAds, totalItems - 1);
  while (indices.size < maxAds) {
    // Avoid index 0 so ads appear between posts, not before the first
    const idx = 1 + Math.floor(Math.random() * (totalItems - 1));
    indices.add(idx);
  }
  return Array.from(indices).sort((a, b) => a - b);
}
</script>

<style scoped lang="scss">
@import '@/styles/abstracts/colors';

.blog-list-view {
  .improved-card {
    border-radius: 16px;
    box-shadow: 0 4px 16px rgba(0,0,0,0.10);
    transition: box-shadow 0.25s, transform 0.25s;
    overflow: hidden;
    background: var(--color-card-background, #fff);
    &:hover {
      box-shadow: 0 8px 32px rgba(0,0,0,0.16);
      transform: translateY(-4px) scale(1.015);
    }
    .blog-image-wrapper {
      position: relative;
      .blog-image-banner {
        margin-bottom: 0;
        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          border-radius: 0;
        }
        .blog-category-badge {
          position: absolute;
          top: 1rem;
          left: 1rem;
          z-index: 2;
          font-size: 0.85em;
          font-weight: 700;
          background: linear-gradient(90deg, $indiefy-blue, $indiefy-lavender);
          color: #fff;
          border: none;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
        }
        .blog-image-gradient {
          position: absolute;
          left: 0; right: 0; bottom: 0; height: 60px;
          background: linear-gradient(0deg, rgba(30,30,40,0.18) 60%, transparent 100%);
          z-index: 1;
        }
      }
    }
    .blog-title {
      font-size: 1.25rem;
      font-weight: 800;
      margin-bottom: 0.5rem;
      color: var(--color-primary, #7189FF);
      line-height: 1.2;
    }
    .blog-meta-row {
      font-size: 0.95rem;
      .is-rounded {
        border-radius: 50%;
      }
    }
    .blog-description {
      max-height: 3.6em;
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      line-clamp: 2;
      -webkit-box-orient: vertical;
      color: var(--color-text, #222);
      opacity: 0.85;
    }
    .blog-readmore-btn {
      border-radius: 999px;
      background: linear-gradient(90deg, $indiefy-blue, $indiefy-lavender);
      color: #fff;
      font-weight: 700;
      border: none;
      box-shadow: 0 2px 8px rgba(0,0,0,0.08);
      padding: 0.5em 1.5em;
      font-size: 1em;
      transition: background 0.2s, box-shadow 0.2s;
      display: inline-flex;
      align-items: center;
      gap: 0.5em;
      &:hover {
        background: linear-gradient(90deg, $indiefy-lavender, $indiefy-blue);
        box-shadow: 0 4px 16px rgba(0,0,0,0.13);
        color: #fff;
      }
    }
  }
}

@media (max-width: 900px) {
  .blog-list-view .improved-card .blog-image-banner img {
    height: 150px;
  }
}
@media (max-width: 600px) {
  .blog-list-view .improved-card {
    border-radius: 10px;
    .blog-image-banner img {
      height: 120px;
    }
    .blog-title {
      font-size: 1.05rem;
    }
    .blog-readmore-btn {
      font-size: 0.95em;
      padding: 0.4em 1em;
    }
  }
}
</style>